import { useEffect } from 'react';
import { faqs } from '../../../mocks/britfit';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'AutoRepair',
  '@id': `${SITE_URL}/#business`,
  name: 'BritFit Mobile Tyres',
  description:
    'BritFit Mobile Tyres — UK\'s trusted mobile tyre fitting service. Fast, professional tyre fitting, puncture repair and wheel balancing delivered to your home, work or roadside.',
  url: SITE_URL,
  telephone: '+44-800-000-0000',
  email: 'admin@britfitmobiletyres.co.uk',
  priceRange: '££',
  currenciesAccepted: 'GBP',
  paymentAccepted: 'Cash, Credit Card, Debit Card, Contactless',
  areaServed: [
    { '@type': 'Country', name: 'United Kingdom' },
    { '@type': 'AdministrativeArea', name: 'England' },
    { '@type': 'AdministrativeArea', name: 'Scotland' },
    { '@type': 'AdministrativeArea', name: 'Wales' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'GB',
    addressRegion: 'United Kingdom',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 54.7023545,
    longitude: -3.2765753,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Mobile Tyre Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Mobile Tyre Fitting',
          description:
            'We come to you — at home, work, or roadside. Our fully-equipped vans carry a wide range of tyres to fit most vehicles on the spot.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Puncture Repair',
          description: 'Fast, reliable puncture repairs using industry-standard techniques.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Wheel Balancing',
          description: 'Eliminate vibrations and uneven wear with precision wheel balancing.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'TPMS Services',
          description: 'Full TPMS sensor diagnostics, replacement and recalibration.',
        },
      },
    ],
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '28000',
    bestRating: '5',
    worstRating: '1',
  },
  sameAs: [],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: SITE_URL,
  name: 'BritFit Mobile Tyres | Professional Mobile Tyre Fitting UK',
  description:
    'BritFit Mobile Tyres — UK\'s trusted mobile tyre fitting service. Fast, professional tyre fitting, puncture repair and wheel balancing delivered to your home, work or roadside.',
  inLanguage: 'en-GB',
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: 'BritFit Mobile Tyres',
    inLanguage: 'en-GB',
  },
};

function injectLdJson(id: string, schema: object): void {
  const existing = document.getElementById(id);
  if (existing) {
    existing.textContent = JSON.stringify(schema);
    return;
  }
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

function removeLdJson(id: string): void {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export default function SEOHead() {
  useEffect(() => {
    // Canonical
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = SITE_URL;

    // Inject JSON-LD schemas
    injectLdJson('ld-local-business', localBusinessSchema);
    injectLdJson('ld-faq', faqSchema);
    injectLdJson('ld-webpage', webPageSchema);

    return () => {
      removeLdJson('ld-local-business');
      removeLdJson('ld-faq');
      removeLdJson('ld-webpage');
    };
  }, []);

  return null;
}
